/**
 * F 157 — the settings store.
 *
 * Settings are ENTITIES. This store owns resolution, validation and the
 * operator rules; persistence is whatever holds the entity list, and schema
 * registration goes through the F 156 `SchemaAdapter` unchanged. **No new
 * adapter method is introduced** — `setting` is just a collection like any
 * other, registered with `registerCollection`.
 *
 * THE OPERATOR RULES ARE HARD, NOT CONFIGURABLE
 * ---------------------------------------------
 * A `global`-scope write requires `operatorApproved`, whatever the collection's
 * PermissionExpr says. An app that can set a global setting can change the
 * behaviour of every other app on the instance, so "who is allowed to write
 * here" is not the same question as "who is allowed to read here". Loosening
 * read is a permission decision; loosening global WRITE is not offered at all.
 *
 * CROSS-SCOPE READS RETURN `undefined`, NEVER SOMEBODY ELSE'S VALUE
 * ---------------------------------------------------------------
 * Resolving `user:alice`'s `editor.fontSize` while reading as `user:bob` must
 * fall THROUGH to the next scope, not leak. The resolution walk tries the
 * requested scope first and only ever matches a setting whose (scope, scopeId)
 * is exactly the one asked for.
 */

import {
  SCOPE_RESOLUTION_ORDER,
  SETTING_SCOPES,
  SETTING_VALUE_TYPES,
  scopeRequiresId,
  valueMatchesType,
  settingId,
  settingFcid,
  type Setting,
  type SettingScope,
  type SettingValueType,
} from '../../types/models/Setting';
import type { SchemaAdapter } from '../schema/adapter';

export type SettingResult =
  | { ok: true; setting: Setting; created: boolean }
  | {
      ok: false;
      code:
        | 'unknown-key'
        | 'empty-key'
        | 'invalid-scope'
        | 'scope-id-required'
        | 'scope-id-forbidden'
        | 'type-mismatch'
        | 'operator-required'
        | 'duplicate'
        | 'not-found';
      message: string;
    };

export interface SetSettingInput {
  key: string;
  value: unknown;
  scope?: SettingScope;
  scopeId?: string | null;
  label?: string;
  description?: string;
  valueType?: SettingValueType;
  default?: unknown;
  editable?: boolean;
  parentId?: string | null;
  order?: number | null;
  operatorApproved?: boolean;
}

export interface ListSettingsInput {
  scope?: SettingScope;
  scopeId?: string | null;
}

export interface SettingStoreOptions {
  /**
   * Optional F 156 schema adapter. When present, `declareCollection()` emits a
   * portable `setting` declaration through it. It is NOT used for reads or
   * writes — settings are entities, and the caller's entity list is the truth.
   */
  adapter?: SchemaAdapter;
  /** Seed entities, e.g. loaded from storage. */
  initial?: Setting[];
}

export class SettingStore {
  private readonly settings = new Map<string, Setting>();
  /** Declared valueType per key, so a write can be checked against the intent. */
  private readonly declared = new Map<string, SettingValueType>();

  constructor(private readonly opts: SettingStoreOptions = {}) {
    for (const s of opts.initial ?? []) this.put(s);
  }

  private put(s: Setting): void {
    this.settings.set(settingId(s.key, s.scope, s.scopeId), s);
    this.declared.set(s.key, s.valueType);
  }

  // ── reads ────────────────────────────────────────────────────────────────

  /**
   * Resolve a key. Most specific scope wins:
   *
   *     user > app > instance > global > the entity's `default` > undefined
   *
   * `scope`/`scopeId` narrow WHICH scope to read. When omitted, all four are
   * walked in resolution order. When given, only that exact (scope, scopeId)
   * matches — which is what stops user B reading user A's value.
   */
  getSetting(key: string, scope?: SettingScope, scopeId?: string | null): unknown {
    // An EXPLICIT scope reads exactly one (scope, scopeId) cell. It does not
    // walk other scopes, which is what isolates user B from user A's value —
    // and why a scoped miss is `undefined` rather than someone else's number.
    if (scope) {
      const hit = this.findExact(key, scope, scopeId);
      if (hit && hit.value !== undefined) return hit.value;
      // Miss. Fall to the key's DECLARED DEFAULT — the default belongs to the
      // key, not to a cell, so a user-specific miss still gets a sane value.
      // It does NOT fall to another scope's VALUE: that is the leak.
      return this.declaredDefault(key);
    }
    // No scope: walk the resolution order. At EACH scope every scopeId is a
    // candidate, because the caller has not said which one they mean.
    for (const sc of SCOPE_RESOLUTION_ORDER) {
      for (const cand of this.candidatesAt(key, sc)) {
        if (cand.value !== undefined) return cand.value;
      }
    }
    return this.declaredDefault(key);
  }

  /** The full Setting entity at an exact (scope, scopeId), if it exists. */
  getSettingEntity(key: string, scope: SettingScope, scopeId?: string | null): Setting | null {
    return this.findExact(key, scope, scopeId) ?? null;
  }

  private findExact(key: string, scope: SettingScope, scopeId?: string | null): Setting | undefined {
    return this.settings.get(settingId(key, scope, scopeId));
  }

  /**
   * Every setting named `key` at exactly this scope, one per scopeId.
   * Ordered by scopeId so a scope-less read is deterministic rather than
   * dependent on Map insertion order.
   */
  private candidatesAt(key: string, scope: SettingScope): Setting[] {
    const out: Setting[] = [];
    for (const s of this.settings.values()) {
      if (s.key === key && s.scope === scope) out.push(s);
    }
    return out.sort((a, b) => String(a.scopeId ?? '').localeCompare(String(b.scopeId ?? '')));
  }

  /**
   * The default carried by the most specific DECLARATION of this key.
   * The default belongs to the key, not to one scopeId's cell, so a read at a
   * scope with nothing set still gets it.
   */
  private declaredDefault(key: string): unknown {
    for (const sc of SCOPE_RESOLUTION_ORDER) {
      for (const d of this.candidatesAt(key, sc)) {
        if (d.default !== undefined) return d.default;
      }
    }
    return undefined;
  }

  listSettings(input: ListSettingsInput = {}): Setting[] {
    return [...this.settings.values()]
      .filter((s) => (input.scope ? s.scope === input.scope : true))
      .filter((s) =>
        input.scopeId === undefined ? true : (s.scopeId ?? null) === (input.scopeId ?? null),
      )
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  allSettings(): Setting[] { return [...this.settings.values()]; }

  // ── writes ───────────────────────────────────────────────────────────────

  setSetting(input: SetSettingInput): SettingResult {
    const scope = input.scope ?? 'global';
    const scopeId = input.scopeId ?? null;

    if (typeof input.key !== 'string' || input.key.trim() === '') {
      return { ok: false, code: 'empty-key', message: 'key must be a non-empty string.' };
    }
    if (!SETTING_SCOPES.includes(scope)) {
      return {
        ok: false, code: 'invalid-scope',
        message: `scope must be one of ${SETTING_SCOPES.join(', ')}. Got ${JSON.stringify(scope)}.`,
      };
    }
    // scopeId is REQUIRED for app/user/instance and FORBIDDEN for global.
    if (scopeRequiresId(scope) && (typeof scopeId !== 'string' || scopeId === '')) {
      return {
        ok: false, code: 'scope-id-required',
        message: `scope "${scope}" requires a scopeId (the app / user / instance id).`,
      };
    }
    if (!scopeRequiresId(scope) && scopeId != null) {
      return {
        ok: false, code: 'scope-id-forbidden',
        message: 'scope "global" must NOT carry a scopeId — global settings are not scoped to anything.',
      };
    }
    // HARD RULE: a global write is operator-only, regardless of permissions.
    if (scope === 'global' && !input.operatorApproved) {
      return {
        ok: false, code: 'operator-required',
        message:
          'Writing a global-scope setting requires operatorApproved. A global setting ' +
          'changes behaviour for every app on the instance.',
      };
    }

    const id = settingId(input.key, scope, scopeId);
    const existing = this.settings.get(id);

    // The declared type wins: a caller cannot retype a setting by writing a
    // value of another type, and a retype is a destructive change.
    const valueType = existing?.valueType ?? input.valueType ?? 'json';
    if (!valueMatchesType(input.value, valueType)) {
      return {
        ok: false, code: 'type-mismatch',
        message: `Setting "${input.key}" is declared ${valueType}; got ${typeof input.value}.`,
      };
    }
    if (existing && existing.valueType !== valueType && !input.operatorApproved) {
      return {
        ok: false, code: 'operator-required',
        message: `Changing the type of "${input.key}" requires operatorApproved.`,
      };
    }

    const now = new Date().toISOString();
    const next: Setting = {
      ...(existing ?? {}),
      id: existing?.id ?? id,
      fcid: existing?.fcid ?? settingFcid(id),
      key: input.key,
      label: input.label ?? existing?.label ?? input.key,
      value: input.value,
      valueType,
      scope,
      scopeId,
      default: input.default ?? existing?.default,
      description: input.description ?? existing?.description,
      editable: input.editable ?? existing?.editable ?? true,
      // Containment and ordering come from the base entity (F 156): a setting
      // can be nested under a settings CATEGORY, and ordered among siblings.
      parentId: input.parentId ?? existing?.parentId ?? null,
      order: input.order ?? existing?.order ?? null,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    this.put(next);
    return { ok: true, setting: next, created: existing === undefined };
  }

  deleteSetting(
    key: string,
    opts: { scope: SettingScope; scopeId?: string | null; operatorApproved: true },
  ): SettingResult {
    const id = settingId(key, opts.scope, opts.scopeId ?? null);
    const existing = this.settings.get(id);
    if (!existing) {
      return { ok: false, code: 'not-found', message: `No setting "${key}" at that scope.` };
    }
    if (!opts.operatorApproved) {
      return {
        ok: false, code: 'operator-required',
        message: 'Deleting a setting requires operatorApproved.',
      };
    }
    this.settings.delete(id);
    return { ok: true, setting: existing, created: false };
  }

  // ── schema registration (F 156 path, unchanged) ───────────────────────────

  /**
   * The portable `setting` declaration, for `registerCollection`.
   *
   * Returned rather than applied: the caller decides when to register, and
   * registration goes through the same gate as every other collection.
   */
  settingCollectionDecl() {
    return {
      name: 'setting',
      fields: [
        { name: 'key', type: 'text' as const, required: true },
        { name: 'label', type: 'text' as const },
        { name: 'value', type: 'json' as const },
        { name: 'valueType', type: 'text' as const, required: true },
        { name: 'scope', type: 'text' as const, required: true },
        { name: 'scopeId', type: 'text' as const },
        { name: 'defaultValue', type: 'json' as const },
        { name: 'description', type: 'text' as const },
        { name: 'editable', type: 'boolean' as const },
        // Base entity fields (F 156) — settings are nodes like any other.
        { name: 'parentId', type: 'text' as const },
        { name: 'order', type: 'number' as const },
      ],
      indexes: ['key', 'scope'],
      // Default-deny, exactly like every other collection (F 156). Listed here
      // explicitly so the intent is visible at the declaration, not inferred.
      permissions: {
        list: 'auth', view: 'auth', create: 'operator', update: 'operator', delete: 'operator',
      },
    };
  }

  /** Convenience: register `setting` through an F 156 adapter. */
  async registerSettingCollection(operatorApproved = false) {
    if (!this.opts.adapter) {
      throw new Error('SettingStore has no SchemaAdapter; register the collection from the host.');
    }
    return this.opts.adapter.emitSchemaMigration(this.settingCollectionDecl() as any, {});
  }
}

export { SCOPE_RESOLUTION_ORDER, SETTING_VALUE_TYPES, settingFcid };
export type { Setting, SettingScope, SettingValueType };
